'use client';

import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { StatusNotification } from '../../StatusNotification/StatusNotification';
import type { StatusNotificationVariant } from '../../StatusNotification/types';
import { useOptionalKycDialog } from '../../global-dialogs/components/KycDialog/context/KycDialogContext';

type SuspendedNotificationWarningProps = {
  namespace: string;
  variant?: StatusNotificationVariant;
  dataTrackMessage?: string;
  dataTrackType?: string;
  buttonText?: string;
  buttonTrackName?: string;
  buttonTestId?: string;
  onButtonClick?: () => void;
};

function SuspendedNotificationWarning({
  namespace,
  variant,
  dataTrackMessage,
  dataTrackType,
  buttonText,
  buttonTrackName,
  buttonTestId,
  onButtonClick,
}: SuspendedNotificationWarningProps) {
  const t = useTranslations(namespace);
  const kycDialog = useOptionalKycDialog();
  const { suspendedAccountCta } = getSiteConfig().MY_ACCOUNT_SETTINGS;

  // A caller's CTA is all or nothing: taking the label without the handler, or the other way round, would pair
  // one caller's wording with another owner's behaviour.
  const overrideCta = buttonText && onButtonClick ? { buttonText, buttonTestId, buttonTrackName, onClick: onButtonClick } : undefined;

  // The market owns the CTA's wording, tracking and test id, so a market that offers no upload route
  // simply has none. Without a KycDialogProvider there is nothing to open, and a CTA that does nothing
  // is worse than no CTA.
  const configuredCta =
    suspendedAccountCta && kycDialog
      ? {
          buttonText: t(suspendedAccountCta.textKey),
          buttonTestId: suspendedAccountCta.testId,
          buttonTrackName: suspendedAccountCta.trackName,
          onClick: kycDialog.openKycDialog,
        }
      : undefined;

  const cta = overrideCta ?? configuredCta;

  return (
    <StatusNotification
      type="error"
      label={t('label')}
      description={t('description')}
      variant={variant}
      // The floating variant lays the icon out beside the text, so only stack the CTA on the fixed variant.
      className={cta && variant !== 'floating' ? 'sm:flex-col sm:items-end' : undefined}
      data-testid="suspended-notification"
      dataTrackMessage={dataTrackMessage}
      dataTrackType={dataTrackType}
      {...cta}
    />
  );
}

export { SuspendedNotificationWarning, type SuspendedNotificationWarningProps };
